/**
 * The design system's semantic tone vocabulary.
 *
 * **One list, shared.** `Status`, `Toast` and `Alert` all report "how did this go",
 * and before this existed each was about to spell that out for itself — which is how
 * a system ends up with `error` in one component, `danger` in the next and
 * `destructive` in a third, all painting the same red.
 *
 * `StatusTone` is this set **plus `accent`**, which only `Status` has: it marks a
 * transient in-progress state (`promoting`) rather than an outcome, and neither a
 * toast nor a banner has a use for it. `__tests__/toneVocabulary.test.ts` pins the
 * subset relationship in both directions so the two cannot drift.
 */
export type SemanticTone = 'neutral' | 'info' | 'success' | 'warning' | 'danger';

export const SEMANTIC_TONES: SemanticTone[] = [
  'neutral',
  'info',
  'success',
  'warning',
  'danger',
];

/**
 * § 358 — the WHOLE tone vocabulary: the six `tone-*` token families in `tokens.css`.
 * Every design-system `tone` prop takes a subset of this, so a new component cannot invent
 * `error`, `critical` or `default` for a colour that already has a name. `neutral` is the
 * word for "no tone": Menu items and ChoiceChip took `'default'`, which is still honoured
 * there as an older spelling and drawn identically.
 *
 * Two documented exceptions, both pinned in `apiContract.test.ts`: Spinner's `inverse` is an
 * ink for a filled ground, not a tone; and Avatar's `tone` is a categorical palette that
 * borrows the tone pairs — its `accent` is the BRAND pair (`accent-tint` under `accent`),
 * which is also what Progress and Spinner mean by `accent`, while its `purple` is the
 * `tone-accent` family that Status calls `accent`. Renaming either repaints the default
 * avatar, so it waits for the token-naming work in Phase 3.
 */
export type Tone = SemanticTone | 'accent';

export const TONES: Tone[] = [...SEMANTIC_TONES, 'accent'];
